import { gsap } from "gsap";
import "./styles.css";
import { DEFAULT_SONG_ID, SONGS } from "./config/songs.js";
import { TextAliveController } from "./core/textalive.js";
import { TimelineAnalyzer } from "./core/timeline.js";
import { LyricRenderer } from "./lyrics/lyricRenderer.js";
import { MemoryCanvas } from "./visuals/memoryCanvas.js";
import { getRenderProfile } from "./utils/performance.js";
import { clamp, damp, hashString, seededUnit } from "./utils/math.js";

const elements = {
  app: document.querySelector("#app"),
  canvas: document.querySelector("#memory-canvas"),
  media: document.querySelector("#media"),
  currentLyric: document.querySelector("#lyric-current"),
  lyricEcho: document.querySelector("#lyric-echo"),
  phraseArchive: document.querySelector("#phrase-archive"),
  intro: document.querySelector("#intro-sequence"),
  ending: document.querySelector("#ending-sequence"),
  endingTitle: document.querySelector("#ending-title"),
  statusDot: document.querySelector("#status-dot"),
  statusText: document.querySelector("#status-text"),
  sectionLabel: document.querySelector("#section-label"),
  songSelect: document.querySelector("#song-select"),
  playButton: document.querySelector("#play-button"),
  pauseButton: document.querySelector("#pause-button"),
  beatMeter: Array.from(document.querySelectorAll(".beat-meter span")),
};

const profile = getRenderProfile();
const controller = new TextAliveController({
  mediaElement: elements.media,
  initialSongId: DEFAULT_SONG_ID,
});
const timeline = new TimelineAnalyzer();
const canvasScene = new MemoryCanvas(elements.canvas, profile);
const lyricRenderer = new LyricRenderer({
  currentElement: elements.currentLyric,
  echoElement: elements.lyricEcho,
  archiveElement: elements.phraseArchive,
  profile,
});

const SECTION_LABELS = {
  intro: "INITIALIZING",
  verse: "VERSE",
  "pre-chorus": "BUILD",
  chorus: "CHORUS",
  ending: "ENDING",
};

let activeSection = "intro";
let localBeatPulse = 0;
let chorusHit = 0;
let camera = { x: 0, y: 0, z: 1 };
let lyricMotion = { x: 0, y: 0, rotate: 0, targetX: 0, targetY: 0, targetRotate: 0 };
let lastFrameTime = performance.now();
let glitchTimer = 0;

initializeSongPicker();
wireControllerEvents();
wireControls();
wireResize();
controller.initialize();
requestAnimationFrame(frame);

function initializeSongPicker() {
  SONGS.forEach((song) => {
    const option = document.createElement("option");
    option.value = song.id;
    option.textContent = `${song.title} - ${song.artist}`;
    elements.songSelect.append(option);
  });
  elements.songSelect.value = DEFAULT_SONG_ID;
}

function wireControllerEvents() {
  controller.on("appReady", (app) => {
    elements.app.dataset.managed = app.managed ? "true" : "false";
    if (app.managed) {
      elements.songSelect.disabled = true;
      elements.playButton.disabled = true;
      elements.pauseButton.disabled = true;
      setStatus({ tone: "ready", text: "Connected to TextAlive host" });
    }
  });

  controller.on("songLoading", (song) => {
    elements.songSelect.value = song?.id || DEFAULT_SONG_ID;
    elements.app.dataset.state = "loading";
    elements.playButton.disabled = true;
    elements.pauseButton.disabled = true;
    elements.songSelect.disabled = true;
    timeline.setSource(null, null);
    timeline.reset(0);
    lyricRenderer.setVideo(null);
    resetStageState();
    showIntro();
  });

  controller.on("videoReady", ({ video, song }) => {
    timeline.setSource(controller.player, video);
    timeline.reset(0);
    lyricRenderer.setVideo(video);
    elements.endingTitle.textContent = song?.title || "Future Idol Memory Stage 2026";
    elements.app.dataset.state = "ready";
    elements.songSelect.disabled = controller.isManaged;
    resetStageState();
  });

  controller.on("timerReady", () => {
    elements.playButton.disabled = controller.isManaged;
    elements.pauseButton.disabled = controller.isManaged;
    elements.songSelect.disabled = controller.isManaged;
  });

  controller.on("play", () => {
    elements.app.dataset.state = "playing";
    hideIntro();
  });

  controller.on("pause", () => {
    elements.app.dataset.state = "paused";
  });

  controller.on("stop", () => {
    elements.app.dataset.state = "ready";
    resetStageState();
    showIntro();
  });

  controller.on("seek", (position) => {
    timeline.reset(position);
  });

  controller.on("status", setStatus);

  controller.on("parameter", ({ name, value }) => {
    canvasScene.setParameter(name, value);
  });
}

function wireControls() {
  elements.playButton.addEventListener("click", () => {
    controller.play();
  });

  elements.pauseButton.addEventListener("click", () => {
    controller.pause();
  });

  elements.songSelect.addEventListener("change", (event) => {
    controller.loadSong(event.target.value);
  });
}

function wireResize() {
  const resizeObserver = new ResizeObserver(() => {
    canvasScene.resize();
    controller.player?.resize?.();
  });
  resizeObserver.observe(elements.app);
}

function frame(time) {
  const delta = Math.min((time - lastFrameTime) / 1000, 0.05);
  lastFrameTime = time;

  const position = controller.getPosition();
  const metrics = controller.getReactiveMetrics(position);
  const snapshot = timeline.update(position, metrics);

  lyricRenderer.update(snapshot);
  handleTimelineEvents(snapshot);
  updateReactiveCss(snapshot, delta, time);
  canvasScene.render(time, snapshot);

  requestAnimationFrame(frame);
}

function handleTimelineEvents(snapshot) {
  if (snapshot.entered?.beat) {
    localBeatPulse = 1;
    canvasScene.onBeat(snapshot.beat);
    updateBeatMeter(snapshot.beat);
  }

  if (snapshot.entered?.word) {
    canvasScene.onWord();
  }

  if (snapshot.entered?.phrase) {
    canvasScene.onPhrase(snapshot.phrase);
    setLyricMotionTarget(snapshot);
  }

  if (snapshot.entered?.chorus) {
    chorusHit = 1;
    canvasScene.onChorus();
    triggerGlitch(420);
    gsap.fromTo(
      elements.app,
      { "--transition-flash": 0.62 },
      { "--transition-flash": 0, duration: 1.25, ease: "expo.out" },
    );
  }

  if (snapshot.section !== activeSection) {
    if (snapshot.section === "pre-chorus" || snapshot.section === "ending") {
      triggerGlitch(180);
    }
    setSection(snapshot.section);
  }
}

function updateReactiveCss(snapshot, delta, time) {
  const t = time * 0.001;
  const chorus = snapshot.chorusIntensity || 0;
  const vocal = snapshot.metrics?.vocal || 0;
  const arousal = snapshot.metrics?.arousal || 0;
  const valence = snapshot.metrics?.valence || 0;
  const mood = snapshot.mood || {};
  const warmth = mood.warmth ?? clamp((valence + 1) * 0.5);
  const moodEnergy = mood.energy ?? 0.3;
  const moodTension = mood.tension ?? 0.2;
  const moodIntimacy = mood.intimacy ?? 0.7;
  const moodRelease = mood.release ?? 0;
  const moodMotion = mood.motion ?? 0.25;
  const emotionalLift = clamp(vocal * 0.42 + moodEnergy * 0.36 + moodRelease * 0.24 + Math.max(0, arousal) * 0.12);
  localBeatPulse = damp(localBeatPulse, 0, 7, delta);
  chorusHit = damp(chorusHit, 0, 2.6, delta);

  const targetX =
    Math.sin(t * 0.18) * (2.4 + moodMotion * 4.8) +
    chorus * Math.sin(t * 0.82) * 6 +
    chorusHit * Math.sin(t * 2.4) * 8 +
    arousal * 2.2;
  const targetY = Math.cos(t * 0.14) * (2 + moodMotion * 3.8) - chorus * 6.5 - chorusHit * 8 - vocal * 2 - moodRelease * 2.8;
  const targetZ = 1 + moodRelease * 0.016 + localBeatPulse * (0.002 + moodEnergy * 0.004) + chorus * 0.006 + chorusHit * 0.014;
  camera.x = damp(camera.x, targetX, 5.5, delta);
  camera.y = damp(camera.y, targetY, 5.5, delta);
  camera.z = damp(camera.z, targetZ, 4.2, delta);

  const driftBreath = profile.reducedMotion ? 0 : Math.sin(t * (0.36 + moodMotion * 0.16)) * (1.8 + moodMotion * 3.2);
  const beatNudge = localBeatPulse * (1.5 + chorus * 2.5 + vocal * 2);
  lyricMotion.x = damp(lyricMotion.x, lyricMotion.targetX, 3.8, delta);
  lyricMotion.y = damp(lyricMotion.y, lyricMotion.targetY, 3.8, delta);
  lyricMotion.rotate = damp(lyricMotion.rotate, lyricMotion.targetRotate, 4.2, delta);

  elements.app.style.setProperty("--camera-x", `${camera.x.toFixed(2)}px`);
  elements.app.style.setProperty("--camera-y", `${camera.y.toFixed(2)}px`);
  elements.app.style.setProperty("--camera-z", camera.z.toFixed(4));
  elements.app.style.setProperty("--lyric-drift-x", `${(lyricMotion.x + driftBreath).toFixed(2)}px`);
  elements.app.style.setProperty("--lyric-drift-y", `${(lyricMotion.y - beatNudge).toFixed(2)}px`);
  elements.app.style.setProperty("--lyric-drift-rotate", `${lyricMotion.rotate.toFixed(3)}deg`);
  elements.app.style.setProperty("--beat-pulse", localBeatPulse.toFixed(3));
  elements.app.style.setProperty("--vocal", vocal.toFixed(3));
  elements.app.style.setProperty("--valence", valence.toFixed(3));
  elements.app.style.setProperty("--warmth", warmth.toFixed(3));
  elements.app.style.setProperty("--arousal", arousal.toFixed(3));
  elements.app.style.setProperty("--emotional-lift", emotionalLift.toFixed(3));
  elements.app.style.setProperty("--mood-energy", moodEnergy.toFixed(3));
  elements.app.style.setProperty("--mood-tension", moodTension.toFixed(3));
  elements.app.style.setProperty("--mood-intimacy", moodIntimacy.toFixed(3));
  elements.app.style.setProperty("--mood-release", moodRelease.toFixed(3));
  elements.app.style.setProperty("--mood-motion", moodMotion.toFixed(3));
  elements.app.style.setProperty("--chorus", chorus.toFixed(3));
  elements.app.style.setProperty("--chorus-hit", chorusHit.toFixed(3));
  elements.app.style.setProperty("--ending", snapshot.endingIntensity.toFixed(3));
}

function updateBeatMeter(beat) {
  const activeIndex = beat ? Math.max(0, (beat.position || 1) - 1) : -1;
  elements.beatMeter.forEach((item, index) => {
    item.classList.toggle("is-active", index === activeIndex);
  });
}

function setSection(section) {
  activeSection = section;
  elements.app.dataset.section = section;
  elements.sectionLabel.textContent = SECTION_LABELS[section] || section.replace("-", " ").toUpperCase();
  elements.ending.setAttribute("aria-hidden", section === "ending" ? "false" : "true");
}

function setLyricMotionTarget(snapshot) {
  if (profile.reducedMotion || !snapshot.phrase) {
    lyricMotion.targetX = 0;
    lyricMotion.targetY = 0;
    lyricMotion.targetRotate = 0;
    return;
  }

  const seed = hashString(`${snapshot.phrase.startTime}:${snapshot.phrase.text}`);
  const chorus = snapshot.chorusIntensity || 0;
  const motion = snapshot.mood?.motion ?? 0.25;
  const release = snapshot.mood?.release ?? 0;
  const phraseLength = snapshot.phrase.charCount || snapshot.phrase.text.length || 1;
  const lengthGuard = clamp(1 - (phraseLength - 12) / 30, 0.45, 1);
  const xRange = (snapshot.section === "chorus" ? 68 : snapshot.section === "pre-chorus" ? 48 : 30) * lengthGuard;
  const yRange = (snapshot.section === "chorus" ? 30 : snapshot.section === "pre-chorus" ? 24 : 16) * lengthGuard;

  lyricMotion.targetX = (seededUnit(seed, 1) - 0.5) * xRange * (1 + motion * 0.35);
  lyricMotion.targetY = (seededUnit(seed, 2) - 0.58) * yRange - chorus * 10 - release * 5;
  lyricMotion.targetRotate = (seededUnit(seed, 3) - 0.5) * (snapshot.section === "chorus" ? 1.2 : 0.55) * lengthGuard;
}

function resetStageState() {
  localBeatPulse = 0;
  chorusHit = 0;
  camera = { x: 0, y: 0, z: 1 };
  lyricMotion = { x: 0, y: 0, rotate: 0, targetX: 0, targetY: 0, targetRotate: 0 };
  elements.app.classList.remove("is-glitching");
  elements.app.style.setProperty("--camera-x", "0px");
  elements.app.style.setProperty("--camera-y", "0px");
  elements.app.style.setProperty("--camera-z", "1");
  elements.app.style.setProperty("--lyric-drift-x", "0px");
  elements.app.style.setProperty("--lyric-drift-y", "0px");
  elements.app.style.setProperty("--lyric-drift-rotate", "0deg");
  elements.app.style.setProperty("--beat-pulse", "0");
  elements.app.style.setProperty("--chorus", "0");
  elements.app.style.setProperty("--chorus-hit", "0");
  elements.app.style.setProperty("--ending", "0");
  elements.app.style.setProperty("--transition-flash", "0");
  updateBeatMeter(null);
  setSection("intro");
}

function setStatus({ tone = "loading", text = "" }) {
  elements.statusText.textContent = text;
  elements.statusDot.dataset.tone = tone;
  if (tone === "error" && !controller.isManaged) {
    elements.songSelect.disabled = false;
  }
}

function hideIntro() {
  gsap.to(elements.intro, {
    autoAlpha: 0,
    y: -28,
    scale: 0.98,
    duration: 0.8,
    ease: "power3.out",
  });
}

function showIntro() {
  gsap.set(elements.intro, { autoAlpha: 1, y: 0, scale: 1 });
}

function triggerGlitch(duration = 220) {
  if (profile.reducedMotion) return;
  window.clearTimeout(glitchTimer);
  elements.app.classList.add("is-glitching");
  glitchTimer = window.setTimeout(() => {
    elements.app.classList.remove("is-glitching");
  }, duration);
}
